import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { X, Plus, Save, ArrowLeft } from "lucide-react";
import { useDecisions, CreateDecisionData } from "@/hooks/useDecisions";
import { useSubscription } from "@/hooks/useSubscription";
import { useProfile } from "@/hooks/useProfile";
import { ValidatedInput, ValidatedRatingSlider, ReviewIntervalValidator } from "./ui/validated-input";
import { client } from "@/integrations/neon/client";
import { Alert, AlertDescription } from "./ui/alert";
import { Crown, Zap } from "lucide-react";
import { DecisionSuccessDialog } from "./DecisionSuccessDialog";
import { generateDecisionReviewCalendar, downloadICSFile } from "@/lib/calendar-utils";
import { toDate, todayISO } from "../utils/date";
import {
  DEFAULT_CONFIDENCE_RATING,
  DEFAULT_REVIEW_INTERVALS,
  getCustomTags,
  getDefaultConfidenceRating,
  getDefaultReviewIntervals,
} from "@/lib/user-settings";

interface NewDecisionFormProps {
  onSave: () => void;
  onCancel: () => void;
  onUpgrade?: () => void;
}

interface DecisionFormData {
  title: string;
  decision_date: string;
  rationale_context: string;
}

const commonEmotions = ["excited", "nervous", "confident", "uncertain", "optimistic", "concerned", "hopeful", "anxious"];

export function NewDecisionForm({ onSave, onCancel, onUpgrade }: NewDecisionFormProps) {
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [emotions, setEmotions] = useState<string[]>([]);
  const [newEmotion, setNewEmotion] = useState("");
  const [reviewIntervals, setReviewIntervals] = useState<number[]>(DEFAULT_REVIEW_INTERVALS);
  const [day0Rating, setDay0Rating] = useState([DEFAULT_CONFIDENCE_RATING]);
  // Seeding happens in an effect, not useState: the profile arrives from the
  // Data API after first render, so an initializer would read null and the
  // user's saved defaults would never be applied.
  const [seededFromSettings, setSeededFromSettings] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [lastCreatedDecision, setLastCreatedDecision] = useState<{
    id: string;
    title: string;
    reviewIntervals: number[];
    decisionDate: Date;
  } | null>(null);
  
  // User's previous tags and emotions
  const [userTags, setUserTags] = useState<string[]>([]);
  const [userEmotions, setUserEmotions] = useState<string[]>([]);
  const [isLoadingUserData, setIsLoadingUserData] = useState(true);

  const { createDecision } = useDecisions();
  const { getReviewDateLimit, canAddReviewDate } = useSubscription();
  const { profile, loading: profileLoading } = useProfile();

  const reviewDateLimit = getReviewDateLimit();
  const canAddAnotherReviewDate = canAddReviewDate(reviewIntervals.length);
  const commonTags = getCustomTags(profile);

  // Apply the defaults saved on the Settings page, once, as soon as the
  // profile is available. `loading` gates it so the built-in fallbacks are not
  // committed before the saved values have had a chance to arrive.
  useEffect(() => {
    if (seededFromSettings || profileLoading) return;
    setReviewIntervals(getDefaultReviewIntervals(profile, reviewDateLimit));
    setDay0Rating([getDefaultConfidenceRating(profile)]);
    setSeededFromSettings(true);
  }, [profile, profileLoading, reviewDateLimit, seededFromSettings]);

  // Fetch user's previously created tags and emotions
  useEffect(() => {
    const fetchUserTagsAndEmotions = async () => {
      try {
        const { data: { user } } = await client.auth.getUser();
        if (!user) return;

        // Fetch user's tags
        const { data: tagsData, error: tagsError } = await client
          .from('tags')
          .select('name')
          .eq('user_id', user.id)
          .order('name');

        if (tagsError) {
          console.error('Error fetching user tags:', tagsError);
        } else {
          setUserTags(tagsData?.map(tag => tag.name) || []);
        }

        // Fetch user's emotions
        const { data: emotionsData, error: emotionsError } = await client
          .from('emotions')
          .select('name')
          .eq('user_id', user.id)
          .order('name');

        if (emotionsError) {
          console.error('Error fetching user emotions:', emotionsError);
        } else {
          setUserEmotions(emotionsData?.map(emotion => emotion.name) || []);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoadingUserData(false);
      }
    };

    fetchUserTagsAndEmotions();
  }, []);

  const { register, handleSubmit, formState: { errors } } = useForm<DecisionFormData>({
    defaultValues: {
      // Local calendar date. toISOString() would give the UTC date, which is
      // already tomorrow (or still yesterday) for part of every day.
      decision_date: todayISO()
    }
  });

  const addTag = (tag: string) => {
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const addEmotion = (emotion: string) => {
    if (emotion && !emotions.includes(emotion)) {
      setEmotions([...emotions, emotion]);
      setNewEmotion("");
    }
  };

  const removeEmotion = (emotionToRemove: string) => {
    setEmotions(emotions.filter(emotion => emotion !== emotionToRemove));
  };


  const updateReviewInterval = (index: number, value: number) => {
    const newIntervals = [...reviewIntervals];
    newIntervals[index] = value;
    setReviewIntervals(newIntervals);
  };

  const removeReviewInterval = (index: number) => {
    setReviewIntervals(reviewIntervals.filter((_, i) => i !== index));
  };

  const addReviewInterval = () => {
    if (!canAddReviewDate(reviewIntervals.length)) return;
    // Default a sensible next interval: a month past the current furthest.
    const furthest = reviewIntervals.reduce((max, i) => Math.max(max, i), 0);
    setReviewIntervals([...reviewIntervals, Math.min(365, furthest + 30)]);
  };

  const getReviewLabel = (index: number, interval: number) => {
    if (interval === 0) return "Day ?";
    
    // Generic labels for the first few intervals
    const labels = ["Day X", "Day Y", "Day Z"];
    if (index < labels.length) {
      return labels[index];
    }
    
    return `Day ${interval}`;
  };

  const getSuggestedDuration = (index: number) => {
    const suggestions = ["suggested 7", "suggested 30", "suggested 90"];
    if (index < suggestions.length) {
      return suggestions[index];
    }
    return "";
  };

  const onSubmit = async (data: DecisionFormData) => {
    setIsSubmitting(true);
    
    try {
      const decisionData: CreateDecisionData = {
        title: data.title,
        description: "", // Empty description
        rationale: data.rationale_context,
        context: "", // Empty context since it's combined with rationale
        decision_date: data.decision_date,
        day0_rating: day0Rating[0],
        review_intervals: reviewIntervals.filter(interval => interval > 0),
        tag_names: tags,
        emotion_names: emotions,
      };

      const decisionId = await createDecision(decisionData);
      
      // Store decision info for calendar generation including the ID
      setLastCreatedDecision({
        id: decisionId,
        title: data.title,
        reviewIntervals: reviewIntervals.filter(interval => interval > 0),
        decisionDate: toDate(data.decision_date)
      });
      
      // Show success dialog instead of immediately calling onSave
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Failed to create decision:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadCalendar = () => {
    if (!lastCreatedDecision) return;
    
    const userTimezone = profile?.timezone || 'UTC';
    const icsContent = generateDecisionReviewCalendar(
      lastCreatedDecision.title,
      lastCreatedDecision.decisionDate,
      lastCreatedDecision.reviewIntervals,
      userTimezone,
      lastCreatedDecision.id
    );
    
    const filename = `decision-reviews-${lastCreatedDecision.title.toLowerCase().replace(/[^a-z0-9]/g, '-')}.ics`;
    downloadICSFile(icsContent, filename);
  };

  const handleSuccessDialogClose = (open: boolean) => {
    setShowSuccessDialog(open);
    if (!open) {
      onSave(); // Navigate back to dashboard when dialog closes
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1>New Decision Entry</h1>
            <p className="text-muted-foreground">Record a new decision for future review and analysis</p>
          </div>
          <Button variant="ghost" onClick={onCancel}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>The core details of your decision</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <ValidatedInput
                  id="title"
                  label="Decision Title *"
                  placeholder="What decision are you making?"
                  {...register("title", { required: "Decision title is required" })}
                />
                {errors.title && (
                  <p className="text-sm text-destructive">{errors.title.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <ValidatedInput
                  id="decision_date"
                  type="date"
                  label="Date"
                  validationType="date"
                  {...register("decision_date", { required: "Date is required" })}
                />
                {errors.decision_date && (
                  <p className="text-sm text-destructive">{errors.decision_date.message}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Decision Details */}
          <Card>
            <CardHeader>
              <CardTitle>Decision Details</CardTitle>
              <CardDescription>The reasoning and context behind your decision</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rationale_context">Rationale & Context *</Label>
                <Textarea
                  id="rationale_context"
                  placeholder="Why are you making this decision? What factors influenced it? What's the broader context (team size, constraints, external factors, etc.)?"
                  rows={6}
                  {...register("rationale_context", { required: "Rationale and context are required" })}
                />
                {errors.rationale_context && (
                  <p className="text-sm text-destructive">{errors.rationale_context.message}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Day 0 Rating */}
          <Card>
            <CardHeader>
              <CardTitle>Initial Confidence</CardTitle>
              <CardDescription>How confident are you about this decision right now?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ValidatedRatingSlider
                value={day0Rating}
                onValueChange={setDay0Rating}
                label="Day 0 Rating"
                showValidation={true}
              />
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
              <CardDescription>Categorize your decision for better organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder="Add a tag..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag(newTag);
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={() => addTag(newTag)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Your Tags</Label>
                <div className="flex flex-wrap gap-2">
                  {commonTags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                      onClick={() => addTag(tag)}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* User's Previous Tags */}
              {!isLoadingUserData && userTags.length > 0 && (
                <div className="space-y-2">
                  <Label>Your Previous Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {userTags
                      .filter(tag => !commonTags.includes(tag)) // Exclude tags that are already in common tags
                      .map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="cursor-pointer hover:bg-secondary hover:text-secondary-foreground border-primary/50"
                          onClick={() => addTag(tag)}
                        >
                          {tag}
                        </Badge>
                      ))
                    }
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Emotions */}
          <Card>
            <CardHeader>
              <CardTitle>Emotions</CardTitle>
              <CardDescription>How are you feeling about this decision?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {emotions.map((emotion) => (
                  <Badge key={emotion} variant="secondary" className="flex items-center gap-1">
                    {emotion}
                    <button
                      type="button"
                      onClick={() => removeEmotion(emotion)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder="Add an emotion..."
                  value={newEmotion}
                  onChange={(e) => setNewEmotion(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addEmotion(newEmotion);
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={() => addEmotion(newEmotion)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Common Emotions</Label>
                <div className="flex flex-wrap gap-2">
                  {commonEmotions.map((emotion) => (
                    <Badge
                      key={emotion}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                      onClick={() => addEmotion(emotion)}
                    >
                      {emotion}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* User's Previous Emotions */}
              {!isLoadingUserData && userEmotions.length > 0 && (
                <div className="space-y-2">
                  <Label>Your Previous Emotions</Label>
                  <div className="flex flex-wrap gap-2">
                    {userEmotions
                      .filter(emotion => !commonEmotions.includes(emotion)) // Exclude emotions that are already in common emotions
                      .map((emotion) => (
                        <Badge
                          key={emotion}
                          variant="outline"
                          className="cursor-pointer hover:bg-secondary hover:text-secondary-foreground border-primary/50"
                          onClick={() => addEmotion(emotion)}
                        >
                          {emotion}
                        </Badge>
                      ))
                    }
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Review Intervals */}
          <Card>
            <CardHeader>
              <CardTitle>Review Schedule</CardTitle>
              <CardDescription>Set when you want to review this decision (in days)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                  {reviewIntervals.map((interval, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Label className="w-20">{getReviewLabel(index, interval)}</Label>
                      <ValidatedInput
                        type="number"
                        min="1"
                        max="365"
                        value={interval}
                        onChange={(e) => updateReviewInterval(index, parseInt(e.target.value) || 0)}
                        className="w-24"
                        validationType="review-interval"
                        showInlineValidation={false}
                      />
                      <span className="text-sm text-muted-foreground">
                        days {getSuggestedDuration(index) && `(${getSuggestedDuration(index)})`}
                      </span>
                      {reviewIntervals.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeReviewInterval(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
              </div>

              {canAddAnotherReviewDate ? (
                <Button type="button" variant="outline" size="sm" onClick={addReviewInterval}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add review date
                </Button>
              ) : (
                <Alert className="border-yellow-200 bg-yellow-50">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="font-medium text-yellow-800">
                        {reviewDateLimit} review dates per decision on the free plan
                      </p>
                      <p className="text-yellow-700">
                        Upgrade to schedule as many reviews as a decision needs.
                      </p>
                      {onUpgrade && (
                        <Button
                          onClick={onUpgrade}
                          variant="outline"
                          size="sm"
                          className="mt-2 border-yellow-300 text-yellow-800 hover:bg-yellow-100"
                        >
                          <Crown className="h-4 w-4 mr-2" />
                          Upgrade
                        </Button>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <ReviewIntervalValidator
                intervals={reviewIntervals.filter(i => i > 0)}
                showDueDates={false}
              />
            </CardContent>
          </Card>

          <Separator />

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pb-6">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              <Save className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Saving...' : 'Save Decision'}
            </Button>
          </div>
        </form>

        <DecisionSuccessDialog
          open={showSuccessDialog}
          onOpenChange={handleSuccessDialogClose}
          onDownloadCalendar={handleDownloadCalendar}
          decisionTitle={lastCreatedDecision?.title || ''}
          reviewCount={lastCreatedDecision?.reviewIntervals.length || 0}
        />
    </div>
  );
}